import { type PayloadAction, createSlice } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { useCallback } from "react";

interface ModalState {
  openModalComponentName: string | null;
  modalData: Record<string, any>;
  modals: Record<string, boolean>;
}

const initialState: ModalState = {
  openModalComponentName: null,
  modalData: {},
  modals: {},
};

const modalSlice = createSlice({
  name: "modal",
  initialState,
  reducers: {
    openModal: (state, action: PayloadAction<{ name: string; data?: Record<string, any> }>) => {
      state.openModalComponentName = action.payload.name;
      state.modalData = action.payload.data || {};
    },
    closeModal: (state) => {
      state.openModalComponentName = null;
      state.modalData = {};
    },
    setModalOpen: (state, action: PayloadAction<{ modalId: string; isOpen: boolean }>) => {
      state.modals[action.payload.modalId] = action.payload.isOpen;
    },
  },
});

export const { openModal, closeModal, setModalOpen } = modalSlice.actions;
export default modalSlice.reducer;

// Hook for accessing modal state (single modal pattern)
export function useModalState() {
  const openModalComponentName = useSelector(
    (state: any) => state.modal.openModalComponentName
  );
  const modalData = useSelector(
    (state: any) => state.modal.modalData
  );

  return {
    openModal: openModalComponentName,
    modalData,
  };
}

// Hook for managing individual modal states (multi-modal pattern)
export function useModal(modalId: string) {
  const dispatch = useDispatch();
  const isOpen = useSelector(
    (state: any) => state.modal.modals[modalId] ?? false
  );

  const open = useCallback(() => {
    dispatch(setModalOpen({ modalId, isOpen: true }));
  }, [dispatch, modalId]);

  const close = useCallback(() => {
    dispatch(setModalOpen({ modalId, isOpen: false }));
  }, [dispatch, modalId]);

  const toggle = useCallback(() => {
    dispatch(setModalOpen({ modalId, isOpen: !isOpen }));
  }, [dispatch, modalId, isOpen]);

  return {
    isOpen,
    open,
    close,
    toggle,
  };
}
